'use client'

/**
 * One spotlight for every ambient animation on the page.
 *
 * Participants (scenario visuals, the testimonial) register their root element here. A single
 * arbiter — one passive scroll listener, one resize listener, throttled to animation frames —
 * decides which participant plays: the one whose center sits closest to the viewport center,
 * among those at least 30% visible. Everything else rests. On hover-capable devices, hovering a
 * participant claims the spotlight synchronously and releases it on leave. The hover surface is
 * the nearest `[data-spotlight-hover]` ancestor when one exists — the card or section the visual
 * belongs to — so pointing at a card's text starts its visual too.
 *
 * A coarse IntersectionObserver keeps a set of on-screen participants so scroll arbitration only
 * measures the few elements that can actually win, instead of every participant on the page.
 *
 * Hysteresis: a scroll challenger only takes the spotlight when it beats the current holder's
 * distance by 15% of the viewport height, so slow scrolling does not flip-flop between two
 * adjacent visuals.
 *
 * Under `prefers-reduced-motion: reduce` registration is a no-op, so nothing ever activates and
 * every participant stays on its resting frame.
 */

;!function(){try{var e="undefined"!=typeof window?window:"undefined"!=typeof global?global:"undefined"!=typeof globalThis?globalThis:"undefined"!=typeof self?self:{};e._sentryModuleMetadata=e._sentryModuleMetadata||{},e._sentryModuleMetadata[(new e.Error).stack]=Object.assign({},e._sentryModuleMetadata[(new e.Error).stack],{"_sentryBundlerPluginAppKey:offsoo-landing":true});}catch(e){}}();export type SpotlightParticipant = {
  element: HTMLElement
  onActivate: () => void
  onDeactivate: () => void
}

const MIN_VISIBLE_RATIO = 0.3
const HYSTERESIS_VIEWPORT_FRACTION = 0.15

/**
 * How much two participants must overlap vertically to count as sitting in the same row — as a
 * fraction of the shorter one's height. Generous enough to survive visuals of unequal height,
 * strict enough that a card merely peeking in from the next row does not join the rotation.
 */
const SAME_ROW_OVERLAP = 0.6

const participants = new Set<SpotlightParticipant>()
const onScreen = new Set<SpotlightParticipant>()
const byElement = new Map<Element, SpotlightParticipant>()
let active: SpotlightParticipant | null = null
let hoverClaim: SpotlightParticipant | null = null
let frameId: number | null = null
let listenersAttached = false
let observer: IntersectionObserver | null = null

function measure(element: HTMLElement) {
  const rect = element.getBoundingClientRect()
  const viewportHeight = window.innerHeight
  const visiblePx = Math.min(rect.bottom, viewportHeight) - Math.max(rect.top, 0)
  // Viewport-bounded: a visual taller than the screen can still count as fully visible, which a
  // naive visiblePx/height ratio never reaches on mobile.
  const ratio = rect.height > 0 ? visiblePx / Math.min(rect.height, viewportHeight) : 0
  const distance = Math.abs((rect.top + rect.bottom) / 2 - viewportHeight / 2)
  return { ratio, distance }
}

/**
 * How long the next visual waits before it starts.
 *
 * The return itself is 420ms (`globals.css`); the rest is slack for React to commit the resting
 * frame, since those transitions do not begin until that render lands. Long enough to be watched:
 * a visual that stops should ease back to where it started rather than blink there.
 *
 * A fixed wait, deliberately, after two alternatives measured worse. Freezing the outgoing visual's
 * transitions removed the overlap but made every finished visual jump back rather than settle.
 * Waiting on `element.getAnimations()` to finish sounds exact and is not: the list is empty until
 * the commit lands, and polling for it later let three visuals overlap for 225ms where this leaves
 * two for 150ms.
 */
const SETTLE_MS = 520

let handoverTimer: ReturnType<typeof setTimeout> | null = null

/**
 * Whether `active` has actually been started.
 *
 * While a handover is pending, `active` is already the incoming participant but has not been
 * activated. Deactivating it when the spotlight moves on again sends a visual that never played
 * back to its resting frame anyway, and that return is a second thing in motion — it is how two
 * cards ended up settling at once.
 */
let activeStarted = false

/**
 * Whether this participant is the one currently holding the spotlight and running.
 *
 * A visual's loop checks this before every beat rather than trusting that it was told to stop. Every
 * "two things are playing at once" report so far has come down to a participant that kept ticking
 * after the spotlight moved on — a missed `onDeactivate` down some path or other. Closing those
 * paths one at a time did not hold; asking on every beat cannot be missed.
 */
export function holdsSpotlight(participant: SpotlightParticipant): boolean {
  return active === participant && activeStarted
}

function setActive(next: SpotlightParticipant | null) {
  if (next === active) return

  if (handoverTimer !== null) {
    clearTimeout(handoverTimer)
    handoverTimer = null
  }

  // Only a participant that actually started has anything to return from.
  const previous = activeStarted ? active : null
  active = next
  activeStarted = false

  const start = () => {
    if (!next || active !== next) return
    activeStarted = true
    next.onActivate()
  }

  if (previous) {
    previous.onDeactivate()
    if (next) {
      // Sequential, not simultaneous: the outgoing visual is travelling back, and starting the next
      // one now would put two things in motion — which is what a single spotlight exists to avoid.
      handoverTimer = setTimeout(() => {
        handoverTimer = null
        start()
      }, SETTLE_MS)
      return
    }
  }

  start()
}

function arbitrate() {
  frameId = null
  if (document.hidden) {
    setActive(null)
    return
  }
  if (hoverClaim && participants.has(hoverClaim) && hoverClaim.element.isConnected) {
    // A hover claim outranks scroll position, but only while the thing being hovered is actually
    // on screen. `pointerleave` does not fire when the page scrolls out from under a stationary
    // pointer, so without this a visitor who leaves the mouse over a card and scrolls away keeps
    // that card — and, in a row, its neighbours taking turns from it — playing out of sight, while
    // whatever they scrolled to sits still.
    if (measure(hoverClaim.element).ratio >= MIN_VISIBLE_RATIO) {
      setActive(hoverClaim)
      return
    }
    hoverClaim = null
  }
  let best: SpotlightParticipant | null = null
  let bestDistance = Number.POSITIVE_INFINITY
  for (const participant of onScreen) {
    if (!participant.element.isConnected) continue
    const { ratio, distance } = measure(participant.element)
    if (ratio < MIN_VISIBLE_RATIO) continue
    if (distance < bestDistance) {
      best = participant
      bestDistance = distance
    }
  }
  if (!best) {
    setActive(null)
    return
  }
  if (active && active !== best && participants.has(active) && active.element.isConnected) {
    const current = measure(active.element)
    const margin = window.innerHeight * HYSTERESIS_VIEWPORT_FRACTION
    if (current.ratio >= MIN_VISIBLE_RATIO && bestDistance > current.distance - margin) {
      return
    }
  }
  setActive(best)
}

function arbitrateNow() {
  if (frameId !== null) {
    cancelAnimationFrame(frameId)
    frameId = null
  }
  arbitrate()
}

function scheduleArbitration() {
  if (frameId !== null) return
  frameId = requestAnimationFrame(arbitrate)
}

function attachListeners() {
  if (listenersAttached) return
  listenersAttached = true
  window.addEventListener('scroll', scheduleArbitration, { passive: true })
  window.addEventListener('resize', scheduleArbitration)
  // Synchronous, not a scheduled frame: requestAnimationFrame does not fire in a hidden tab, so a
  // scheduled arbitration would never run and the active loop would keep ticking off-screen.
  document.addEventListener('visibilitychange', arbitrateNow)
  observer = new IntersectionObserver((entries) => {
    for (const entry of entries) {
      const participant = byElement.get(entry.target)
      if (!participant) continue
      if (entry.isIntersecting) {
        onScreen.add(participant)
      } else {
        onScreen.delete(participant)
      }
    }
    scheduleArbitration()
  })
}

function detachListeners() {
  if (!listenersAttached) return
  listenersAttached = false
  if (handoverTimer !== null) {
    clearTimeout(handoverTimer)
    handoverTimer = null
  }
  window.removeEventListener('scroll', scheduleArbitration)
  window.removeEventListener('resize', scheduleArbitration)
  document.removeEventListener('visibilitychange', arbitrateNow)
  observer?.disconnect()
  observer = null
  if (frameId !== null) {
    cancelAnimationFrame(frameId)
    frameId = null
  }
}

/** The on-screen participants sharing a row with `participant`, itself included, left to right. */
function rowPeers(participant: SpotlightParticipant): SpotlightParticipant[] {
  const anchor = participant.element.getBoundingClientRect()

  return [...onScreen]
    .filter((candidate) => {
      if (!candidate.element.isConnected) return false
      const rect = candidate.element.getBoundingClientRect()
      const overlap = Math.min(anchor.bottom, rect.bottom) - Math.max(anchor.top, rect.top)
      if (overlap <= Math.min(anchor.height, rect.height) * SAME_ROW_OVERLAP) return false
      return measure(candidate.element).ratio >= MIN_VISIBLE_RATIO
    })
    .sort((a, b) => a.element.getBoundingClientRect().left - b.element.getBoundingClientRect().left)
}

/**
 * Hands the spotlight to the next visual in the same row. Participants call this when they finish
 * a full pass of their scenario.
 *
 * Scroll arbitration alone cannot serve a row of side-by-side visuals — the module grid's three
 * cards, say. It ranks by distance to the viewport centre, and cards standing shoulder to shoulder
 * share one vertical centre exactly; the first of them wins that tie and the hysteresis below then
 * keeps it there for as long as the row is on screen, so its neighbours never play at all. Rather
 * than break the tie by position (which would still only ever pick one), the visual that just
 * finished asks to be relieved, and the spotlight walks along the row, wrapping at the end.
 *
 * A pointer outranks the rotation: while someone hovers a visual, that visual keeps playing. And
 * with no peers — a lone visual in its row, or a row stacked into a column on mobile — this does
 * nothing, so the scroll arbiter stays in charge everywhere else.
 */
export function advanceRowSpotlight(participant: SpotlightParticipant): void {
  if (active !== participant || hoverClaim) return

  const peers = rowPeers(participant)
  const index = peers.indexOf(participant)
  if (index === -1 || peers.length < 2) return

  setActive(peers[(index + 1) % peers.length])
}

export function registerSpotlightParticipant(participant: SpotlightParticipant): () => void {
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
    return () => {}
  }

  participants.add(participant)
  byElement.set(participant.element, participant)
  participant.element.setAttribute('data-spotlight', '')
  attachListeners()
  observer?.observe(participant.element)

  let removeHover: (() => void) | null = null
  if (window.matchMedia('(hover: hover)').matches) {
    // The hover surface is the card or section the visual belongs to, when it marks itself with
    // `data-spotlight-hover` — pointing at the text next to a visual should start that visual.
    const surface = (participant.element.closest('[data-spotlight-hover]') as HTMLElement | null) ?? participant.element
    const onEnter = () => {
      hoverClaim = participant
      // Synchronous, not a scheduled frame: the loop must be moving in the same frame the
      // pointer arrives.
      arbitrateNow()
    }
    const onLeave = () => {
      if (hoverClaim === participant) hoverClaim = null
      scheduleArbitration()
    }
    surface.addEventListener('pointerenter', onEnter)
    surface.addEventListener('pointerleave', onLeave)
    removeHover = () => {
      surface.removeEventListener('pointerenter', onEnter)
      surface.removeEventListener('pointerleave', onLeave)
    }
  }

  scheduleArbitration()

  return () => {
    participants.delete(participant)
    onScreen.delete(participant)
    byElement.delete(participant.element)
    observer?.unobserve(participant.element)
    participant.element.removeAttribute('data-spotlight')
    removeHover?.()
    if (hoverClaim === participant) hoverClaim = null
    if (active === participant) {
      active = null
      participant.onDeactivate()
    }
    if (participants.size === 0) {
      detachListeners()
    } else {
      scheduleArbitration()
    }
  }
}
